import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Upload, Search, Rss, CheckCircle, AlertCircle, Loader2, Link2, Clock, Globe, Newspaper, Radio, Settings } from 'lucide-react';
import { ingestContentItem, runWebSearch, ingestUrl, listIntegrations, listPulsarSearches, createPulsarSearch, previewPulsarImport, getPulsarPreviewStatus, runPulsarImport, listPulsarSourceTiers } from '../services/api';
import type { WebSearchResult, PulsarSearch, PulsarPreviewStatus, PulsarImportResult, PulsarSourceTier, NumericRange } from '../services/api';
import { ChipSelect } from '../components/pulsar/ChipSelect';
import { PULSAR_HISTORIC_CATEGORIES } from '../components/pulsar/options';

type Tab = 'document' | 'web' | 'rss' | 'pulsar';

export function IngestionPage() {
  const [activeTab, setActiveTab] = useState<Tab>('document');

  return (
    <div className="p-6 max-w-2xl mx-auto space-y-5">
      <h2 className="text-xl font-semibold">Acquisizione fonti</h2>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-900 border border-gray-800 rounded-lg p-1 self-start">
        {([
          { id: 'document', label: 'Documento', icon: <Upload className="w-4 h-4" /> },
          { id: 'web',      label: 'Ricerca Web', icon: <Search className="w-4 h-4" /> },
          { id: 'rss',      label: 'News RSS', icon: <Rss className="w-4 h-4" /> },
          { id: 'pulsar',   label: 'Pulsar', icon: <Radio className="w-4 h-4" /> },
        ] as { id: Tab; label: string; icon: React.ReactNode }[]).map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 text-sm rounded-md font-medium transition-colors ${
              activeTab === tab.id
                ? 'bg-blue-600 text-white'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'document' && <DocumentTab />}
      {activeTab === 'web'      && <WebTab />}
      {activeTab === 'rss'      && <RssTab />}
      {activeTab === 'pulsar'   && <PulsarTab />}
    </div>
  );
}

// ─── Document upload tab ──────────────────────────────────────────────────────

function DocumentTab() {
  const [dragging, setDragging]   = useState(false);
  const [file, setFile]           = useState<File | null>(null);
  const [title, setTitle]         = useState('');
  const [uploading, setUploading] = useState(false);
  const [result, setResult]       = useState<{ id: string; is_duplicate: boolean; pipeline_status: string } | null>(null);
  const [error, setError]         = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const ACCEPTED = '.pdf,.docx,.doc,.txt';
  const MAX_MB   = 50;

  const validateFile = (f: File): string | null => {
    const allowed = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/msword', 'text/plain'];
    if (!allowed.includes(f.type)) return `Tipo file non supportato: ${f.type}`;
    if (f.size > MAX_MB * 1024 * 1024) return `File troppo grande (max ${MAX_MB}MB)`;
    return null;
  };

  const handleFile = (f: File) => {
    const err = validateFile(f);
    if (err) { setError(err); return; }
    setFile(f);
    setError(null);
    setResult(null);
    if (!title) setTitle(f.name.replace(/\.[^.]+$/, ''));
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files[0];
    if (f) handleFile(f);
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const res = await ingestContentItem(file, title || undefined);
      setResult(res);
      setFile(null);
      setTitle('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore upload');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Drop zone */}
      <div
        onDrop={handleDrop}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onClick={() => inputRef.current?.click()}
        className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-colors ${
          dragging ? 'border-blue-500 bg-blue-900/20' : 'border-gray-700 hover:border-gray-500 hover:bg-gray-800/40'
        }`}
      >
        <Upload className="w-10 h-10 mx-auto mb-3 text-gray-500" />
        <p className="text-gray-300 text-sm font-medium">
          {file ? file.name : 'Trascina un file qui o clicca per selezionare'}
        </p>
        <p className="text-gray-500 text-xs mt-1">PDF, DOCX, TXT · max {MAX_MB}MB</p>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED}
          className="hidden"
          onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }}
        />
      </div>

      {/* Title */}
      <div>
        <label className="block text-xs text-gray-400 mb-1.5">Titolo (opzionale)</label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Titolo del documento"
          className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
        />
      </div>

      {error && (
        <div className="flex items-center gap-2 text-red-400 text-sm bg-red-900/20 border border-red-800 rounded-lg px-4 py-2">
          <AlertCircle className="w-4 h-4 shrink-0" />
          {error}
        </div>
      )}

      {result && (
        <div className="flex items-center gap-2 text-green-400 text-sm bg-green-900/20 border border-green-800 rounded-lg px-4 py-2">
          <CheckCircle className="w-4 h-4 shrink-0" />
          {result.is_duplicate ? 'Documento già presente.' : `Documento caricato — in elaborazione (${result.pipeline_status})`}
        </div>
      )}

      <button
        onClick={handleUpload}
        disabled={!file || uploading}
        className="w-full bg-blue-600 hover:bg-blue-500 disabled:bg-gray-700 disabled:text-gray-500 text-white text-sm py-2.5 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
      >
        {uploading ? <><Loader2 className="w-4 h-4 animate-spin" /> Caricamento...</> : <><Upload className="w-4 h-4" /> Carica documento</>}
      </button>
    </div>
  );
}

// ─── Web research tab ─────────────────────────────────────────────────────────

interface TimeRangeOption {
  label: string;
  short: string;
  days:  number | undefined;
}

const TIME_RANGE_OPTIONS: TimeRangeOption[] = [
  { label: 'Ultime 24h',    short: '24h',  days: 1 },
  { label: 'Ultimi 7 giorni', short: '7g',  days: 7 },
  { label: 'Ultimi 30 giorni', short: '30g', days: 30 },
  { label: 'Qualsiasi data', short: 'Tutto', days: undefined },
];

function SearchResultCard({ result, onGoToInbox }: { result: WebSearchResult; onGoToInbox: () => void }) {
  const hasNew = result.saved > 0;
  return (
    <div className={`rounded-xl border p-4 space-y-3 ${hasNew ? 'bg-green-900/15 border-green-800' : 'bg-gray-800/60 border-gray-700'}`}>
      <div className="flex items-center gap-2">
        <CheckCircle className={`w-4 h-4 shrink-0 ${hasNew ? 'text-green-400' : 'text-gray-400'}`} />
        <span className={`text-sm font-medium ${hasNew ? 'text-green-300' : 'text-gray-300'}`}>
          {hasNew ? `${result.saved} nuovi articoli salvati in Inbox` : 'Nessun nuovo articolo trovato'}
        </span>
      </div>
      <div className="grid grid-cols-3 gap-2 text-center">
        <div className="bg-gray-900/60 rounded-lg py-2 px-1">
          <p className="text-lg font-bold text-white">{result.totalFound}</p>
          <p className="text-xs text-gray-500">trovati</p>
        </div>
        <div className="bg-gray-900/60 rounded-lg py-2 px-1">
          <p className="text-lg font-bold text-yellow-400">{result.duplicates}</p>
          <p className="text-xs text-gray-500">già presenti</p>
        </div>
        <div className="bg-gray-900/60 rounded-lg py-2 px-1">
          <p className="text-lg font-bold text-gray-400">{result.skipped}</p>
          <p className="text-xs text-gray-500">saltati</p>
        </div>
      </div>
      {hasNew && (
        <button
          onClick={onGoToInbox}
          className="w-full text-xs text-blue-400 hover:text-blue-300 transition-colors text-center pt-1"
        >
          Vai all'Inbox per approvare →
        </button>
      )}
    </div>
  );
}

function WebTab() {
  const navigate = useNavigate();
  const [query, setQuery]           = useState('');
  const [maxResults, setMaxResults] = useState(10);
  const [topic, setTopic]           = useState<'news' | 'general'>('news');
  const [days, setDays]             = useState<number | undefined>(7);
  const [loading, setLoading]       = useState(false);
  const [result, setResult]         = useState<WebSearchResult | null>(null);
  const [error, setError]           = useState<string | null>(null);
  const [urlInput, setUrlInput]     = useState('');
  const [urlLoading, setUrlLoading] = useState(false);
  const [urlResult, setUrlResult]   = useState<{ title: string; message: string } | null>(null);
  const [urlError, setUrlError]     = useState<string | null>(null);

  const handleSearch = async () => {
    if (!query.trim()) return;
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const res = await runWebSearch({ query, maxResults, topic, days });
      setResult(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore ricerca');
    } finally {
      setLoading(false);
    }
  };

  const handleUrlFetch = async () => {
    const url = urlInput.trim();
    if (!url) return;
    setUrlLoading(true);
    setUrlError(null);
    setUrlResult(null);
    try {
      const res = await ingestUrl(url);
      setUrlResult({ title: res.title, message: res.message });
      setUrlInput('');
    } catch (err) {
      setUrlError(err instanceof Error ? err.message : 'Errore fetch URL');
    } finally {
      setUrlLoading(false);
    }
  };

  const sliderMax = topic === 'news' ? 20 : 20;

  return (
    <div className="space-y-6">

      {/* ── Ricerca automatica Brave ── */}
      <div className="space-y-4">

        {/* Header con badge motore */}
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-300">Ricerca automatica</h3>
          <span className="flex items-center gap-1.5 text-xs font-medium bg-orange-900/40 border border-orange-800/60 text-orange-300 px-2.5 py-1 rounded-full">
            <Globe className="w-3 h-3" />
            Brave Search
          </span>
        </div>

        {/* Query */}
        <div>
          <label className="block text-xs text-gray-400 mb-1.5">Argomento da ricercare</label>
          <input
            type="text"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setResult(null); setError(null); }}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            placeholder="es. accordi Italia Libia 2024"
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
          />
        </div>

        {/* Topic */}
        <div>
          <label className="block text-xs text-gray-400 mb-1.5">Tipo di ricerca</label>
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => { setTopic('news'); setResult(null); }}
              className={`flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium border transition-colors ${
                topic === 'news'
                  ? 'bg-blue-700 border-blue-500 text-white'
                  : 'bg-gray-800 border-gray-700 text-gray-400 hover:text-white hover:border-gray-500'
              }`}
            >
              <Newspaper className="w-4 h-4" />
              Notizie
            </button>
            <button
              onClick={() => { setTopic('general'); setResult(null); }}
              className={`flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium border transition-colors ${
                topic === 'general'
                  ? 'bg-blue-700 border-blue-500 text-white'
                  : 'bg-gray-800 border-gray-700 text-gray-400 hover:text-white hover:border-gray-500'
              }`}
            >
              <Search className="w-4 h-4" />
              Generale
            </button>
          </div>
          <p className="text-xs text-gray-600 mt-1.5">
            {topic === 'news'
              ? 'Articoli di attualità e notizie recenti (endpoint /news/search)'
              : 'Qualsiasi fonte web: blog, wiki, documenti (endpoint /web/search)'}
          </p>
        </div>

        {/* Time range */}
        <div>
          <label className="block text-xs text-gray-400 mb-1.5 flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5" /> Intervallo temporale
          </label>
          <div className="grid grid-cols-4 gap-1.5">
            {TIME_RANGE_OPTIONS.map((opt) => (
              <button
                key={String(opt.days)}
                onClick={() => { setDays(opt.days); setResult(null); }}
                title={opt.label}
                className={`py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                  days === opt.days
                    ? 'bg-teal-700 border-teal-500 text-white'
                    : 'bg-gray-800 border-gray-700 text-gray-400 hover:text-white hover:border-gray-500'
                }`}
              >
                {opt.short}
              </button>
            ))}
          </div>
        </div>

        {/* Max results slider */}
        <div>
          <label className="block text-xs text-gray-400 mb-1.5">
            Risultati da recuperare: <span className="text-white font-semibold">{maxResults}</span>
            <span className="text-gray-600 ml-1">(max {sliderMax})</span>
          </label>
          <input
            type="range" min={3} max={sliderMax} step={1} value={Math.min(maxResults, sliderMax)}
            onChange={(e) => setMaxResults(Number(e.target.value))}
            className="w-full accent-blue-500"
          />
        </div>

        {/* Italy info */}
        <div className="flex items-center gap-2 text-xs text-gray-500 bg-gray-800/40 border border-gray-800 rounded-lg px-3 py-2">
          <span className="text-base leading-none">🇮🇹</span>
          <span>Ricerca circoscritta all'Italia — <code className="text-gray-400">search_lang=it</code> · <code className="text-gray-400">country=IT</code></span>
        </div>

        {error && (
          <div className="flex items-center gap-2 text-red-400 text-sm bg-red-900/20 border border-red-800 rounded-lg px-4 py-2">
            <AlertCircle className="w-4 h-4 shrink-0" /> {error}
          </div>
        )}

        {result && (
          <SearchResultCard result={result} onGoToInbox={() => navigate('/inbox')} />
        )}

        <button
          onClick={handleSearch}
          disabled={!query.trim() || loading}
          className="w-full bg-blue-600 hover:bg-blue-500 disabled:bg-gray-700 disabled:text-gray-500 text-white text-sm py-2.5 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
        >
          {loading
            ? <><Loader2 className="w-4 h-4 animate-spin" /> Ricerca in corso…</>
            : <><Search className="w-4 h-4" /> Avvia ricerca</>}
        </button>
      </div>

      {/* ── Carica da URL ── */}
      <div className="border-t border-gray-800 pt-5 space-y-3">
        <div className="flex items-center gap-2">
          <Link2 className="w-4 h-4 text-gray-400" />
          <h3 className="text-sm font-semibold text-gray-300">Carica da URL specifico</h3>
        </div>
        <p className="text-xs text-gray-600">
          Incolla l'URL di un articolo per estrarne il testo completo e aggiungerlo all'Inbox.
        </p>
        <div className="flex gap-2">
          <input
            type="url"
            value={urlInput}
            onChange={(e) => { setUrlInput(e.target.value); setUrlResult(null); setUrlError(null); }}
            onKeyDown={(e) => e.key === 'Enter' && handleUrlFetch()}
            placeholder="https://esempio.it/articolo"
            className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
          />
          <button
            onClick={handleUrlFetch}
            disabled={!urlInput.trim() || urlLoading}
            className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-500 disabled:bg-gray-700 disabled:text-gray-500 text-white text-sm px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap"
          >
            {urlLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Link2 className="w-3.5 h-3.5" /> Fetch</>}
          </button>
        </div>
        {urlError && (
          <div className="flex items-center gap-2 text-red-400 text-sm bg-red-900/20 border border-red-800 rounded-lg px-4 py-2">
            <AlertCircle className="w-4 h-4 shrink-0" /> {urlError}
          </div>
        )}
        {urlResult && (
          <div className="bg-green-900/15 border border-green-800 rounded-lg px-4 py-3 space-y-1">
            <div className="flex items-center gap-2 text-green-400 text-sm font-medium">
              <CheckCircle className="w-4 h-4 shrink-0" /> Articolo aggiunto all'Inbox
            </div>
            {urlResult.title && (
              <p className="text-xs text-gray-400 pl-6 truncate">{urlResult.title}</p>
            )}
            <button
              onClick={() => navigate('/inbox')}
              className="text-xs text-blue-400 hover:text-blue-300 transition-colors pl-6"
            >
              Vai all'Inbox →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Pulsar Media Intelligence tab ────────────────────────────────────────────

// Categorie che ammettono i filtri "media reach" (news + broadcast, per cui
// Pulsar espone lo stesso concetto di reach) vs. i filtri "engagement/follower"
// (canali social/community, dove il segnale rilevante è l'interazione, non il reach).
const NEWS_REACH_CATEGORIES   = ['ONLINE_NEWS', 'TV', 'RADIO', 'PODCAST'];
const SOCIAL_ENGAGEMENT_CATEGORIES = ['X', 'REDDIT', 'FORUMS', 'BLOGS', 'REVIEWS'];

const NEWS_SUBTYPES: { value: string; label: string }[] = [
  { value: 'ORIGINAL_NEWS_ARTICLE',    label: 'Articolo originale' },
  { value: 'SYNDICATED_NEWS_ARTICLE',  label: 'Articolo sindacato' },
];

const SENTIMENTS: { value: string; label: string }[] = [
  { value: 'POSITIVE', label: 'Positivo' },
  { value: 'NEGATIVE', label: 'Negativo' },
  { value: 'NEUTRAL',  label: 'Neutro' },
];

const CREDIBILITY_LABELS: { value: string; label: string }[] = [
  { value: 'CREDIBLE',               label: 'Credibile' },
  { value: 'USER_GENERATED_CONTENT', label: 'Contenuto utente' },
  { value: 'NON_CREDIBLE',           label: 'Non credibile' },
  { value: 'SATIRE',                 label: 'Satira' },
];

function RangeInput({ label, value, onChange }: { label: string; value: NumericRange; onChange: (v: NumericRange) => void }) {
  return (
    <div>
      <label className="block text-xs text-gray-400 mb-1.5">{label}</label>
      <div className="flex items-center gap-2">
        <input
          type="number" placeholder="min" value={value.min ?? ''}
          onChange={(e) => onChange({ ...value, min: e.target.value === '' ? undefined : Number(e.target.value) })}
          className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-blue-500"
        />
        <span className="text-gray-600 text-xs shrink-0">a</span>
        <input
          type="number" placeholder="max" value={value.max ?? ''}
          onChange={(e) => onChange({ ...value, max: e.target.value === '' ? undefined : Number(e.target.value) })}
          className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-blue-500"
        />
      </div>
    </div>
  );
}

function todayIsoDate(): string {
  return new Date().toISOString().slice(0, 10);
}

function daysAgoIsoDate(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

function PulsarTab() {
  const navigate = useNavigate();

  const [configured, setConfigured] = useState<boolean | null>(null);
  const [searches, setSearches]     = useState<PulsarSearch[]>([]);
  const [searchesLoading, setSearchesLoading] = useState(false);
  const [searchesError, setSearchesError]     = useState<string | null>(null);

  const [selectedSearchId, setSelectedSearchId] = useState<string>('');
  const [creatingNew, setCreatingNew]           = useState(false);
  const [newName, setNewName]                   = useState('');
  const [newBooleanExpr, setNewBooleanExpr]     = useState('');
  const [newCategories, setNewCategories]       = useState<string[]>(['ONLINE_NEWS']);
  const [createLoading, setCreateLoading]       = useState(false);
  const [createError, setCreateError]           = useState<string | null>(null);

  const [startDate, setStartDate] = useState(daysAgoIsoDate(7));
  const [endDate, setEndDate]     = useState(todayIsoDate());
  const [previewCategories, setPreviewCategories] = useState<string[]>(['ONLINE_NEWS']);

  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError]     = useState<string | null>(null);
  const [previewStatus, setPreviewStatus]   = useState<PulsarPreviewStatus | null>(null);

  const [limit, setLimit] = useState(30);
  const [importLoading, setImportLoading] = useState(false);
  const [importError, setImportError]     = useState<string | null>(null);
  const [importResult, setImportResult]   = useState<PulsarImportResult | null>(null);

  // Filtri granulari — si applicano solo in fase di import (§11.2 wiki:
  // CreateHistoricInput/preview non li supporta, solo categorie+periodo).
  const [sourceTiers, setSourceTiers]           = useState<PulsarSourceTier[]>([]);
  const [selectedTiers, setSelectedTiers]       = useState<string[]>([]);
  const [selectedNewsSubtypes, setSelectedNewsSubtypes] = useState<string[]>([]);
  const [mediaReach, setMediaReach]             = useState<NumericRange>({});
  const [engagements, setEngagements]           = useState<NumericRange>({});
  const [followers, setFollowers]               = useState<NumericRange>({});
  const [selectedSentiments, setSelectedSentiments]     = useState<string[]>([]);
  const [selectedCredibility, setSelectedCredibility]   = useState<string[]>([]);

  const showNewsFilters   = previewCategories.some((c) => c === 'ONLINE_NEWS');
  const showReachFilter   = previewCategories.some((c) => NEWS_REACH_CATEGORIES.includes(c));
  const showSocialFilters = previewCategories.some((c) => SOCIAL_ENGAGEMENT_CATEGORIES.includes(c));

  // Pulsar restituisce id come numero JSON (es. 214778), ma <select>/e.target.value
  // sono sempre stringhe — confronto normalizzato per evitare un mismatch di tipo
  // che farebbe risultare selectedSearch sempre null (nessuna UI dopo la selezione).
  const selectedSearch = searches.find((s) => String(s.id) === String(selectedSearchId)) || null;

  useEffect(() => {
    listPulsarSourceTiers().then(setSourceTiers).catch(() => {});
  }, []);

  // ── Stato integrazione + elenco ricerche ──────────────────────────────────
  useEffect(() => {
    listIntegrations()
      .then((integrations) => {
        const pulsar = integrations.find((i) => i.platform === 'pulsar');
        const isConfigured = !!pulsar && pulsar.has_credentials && pulsar.active;
        setConfigured(isConfigured);
        if (isConfigured) {
          setSearchesLoading(true);
          listPulsarSearches()
            .then((r) => setSearches(r.searches))
            .catch((err) => setSearchesError(err instanceof Error ? err.message : 'Errore caricamento ricerche'))
            .finally(() => setSearchesLoading(false));
        }
      })
      .catch(() => setConfigured(false));
  }, []);

  const toggleCategory = (list: string[], setList: (v: string[]) => void, value: string) => {
    setList(list.includes(value) ? list.filter((c) => c !== value) : [...list, value]);
  };

  const handleCreateSearch = async () => {
    if (!newName.trim() || !newBooleanExpr.trim() || newCategories.length === 0) return;
    setCreateLoading(true);
    setCreateError(null);
    try {
      const search = await createPulsarSearch({
        name: newName.trim(),
        booleanExpression: newBooleanExpr.trim(),
        categories: newCategories,
      });
      setSearches((prev) => [...prev, search]);
      setSelectedSearchId(search.id);
      setCreatingNew(false);
      setNewName('');
      setNewBooleanExpr('');
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Errore creazione ricerca');
    } finally {
      setCreateLoading(false);
    }
  };

  const pollPreview = async (historicId: number, searchId: string) => {
    // Polling lato client: il calcolo Pulsar della stima può richiedere
    // 30-40s+ — verificato dal vivo. Max ~2 minuti di tentativi.
    for (let attempt = 0; attempt < 24; attempt++) {
      await new Promise((r) => setTimeout(r, 5000));
      try {
        const status = await getPulsarPreviewStatus(historicId, searchId);
        setPreviewStatus(status);
        if (status.ready) return;
      } catch (err) {
        setPreviewError(err instanceof Error ? err.message : 'Errore stima volume');
        return;
      }
    }
  };

  const handlePreview = async () => {
    if (!selectedSearch || previewCategories.length === 0) return;
    setPreviewLoading(true);
    setPreviewError(null);
    setPreviewStatus(null);
    setImportResult(null);
    try {
      const historic = await previewPulsarImport({
        searchId: selectedSearch.id,
        categories: previewCategories,
        startDate,
        endDate,
      });
      setPreviewStatus({ historicId: historic.historicId, status: historic.status, previewResult: null, sampleSize: null, ready: false });
      await pollPreview(historic.historicId, selectedSearch.id);
    } catch (err) {
      setPreviewError(err instanceof Error ? err.message : 'Errore anteprima volume');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleImport = async () => {
    if (!selectedSearch || !previewStatus?.ready) return;
    setImportLoading(true);
    setImportError(null);
    try {
      const result = await runPulsarImport({
        historicId: previewStatus.historicId,
        searchId:   selectedSearch.id,
        searchHash: selectedSearch.searchHash,
        dateFrom:   startDate,
        dateTo:     endDate,
        limit,
        filters: {
          sourceTiers:       showNewsFilters   && selectedTiers.length > 0         ? selectedTiers         : undefined,
          newsSubtypes:      showNewsFilters   && selectedNewsSubtypes.length > 0  ? selectedNewsSubtypes  : undefined,
          mediaReach:        showReachFilter   ? mediaReach   : undefined,
          engagements:       showSocialFilters ? engagements  : undefined,
          followers:         showSocialFilters ? followers    : undefined,
          sentiments:        selectedSentiments.length > 0 ? selectedSentiments : undefined,
          credibilityLabels: selectedCredibility.length > 0 ? selectedCredibility : undefined,
        },
      });
      setImportResult(result);
      setPreviewStatus(null);
    } catch (err) {
      setImportError(err instanceof Error ? err.message : 'Errore import');
    } finally {
      setImportLoading(false);
    }
  };

  if (configured === null) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-500">
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }

  if (!configured) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-gray-500 space-y-3 text-center">
        <Radio className="w-12 h-12 text-gray-700" />
        <p className="text-sm max-w-sm">
          Integrazione Pulsar non configurata. Collega il tuo account Pulsar per importare contenuti media/social.
        </p>
        <Link
          to="/settings"
          className="flex items-center gap-1.5 text-sm text-blue-400 hover:text-blue-300 transition-colors"
        >
          <Settings className="w-3.5 h-3.5" /> Vai a Impostazioni
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-300">Pulsar Media Intelligence</h3>
        <span className="flex items-center gap-1.5 text-xs font-medium bg-purple-900/40 border border-purple-800/60 text-purple-300 px-2.5 py-1 rounded-full">
          <Radio className="w-3 h-3" /> Pulsar
        </span>
      </div>

      {/* Selezione ricerca */}
      <div>
        <label className="block text-xs text-gray-400 mb-1.5">Ricerca</label>
        {searchesLoading ? (
          <div className="flex items-center gap-2 text-xs text-gray-500"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Caricamento ricerche…</div>
        ) : searchesError ? (
          <p className="text-xs text-red-400">{searchesError}</p>
        ) : (
          <div className="flex gap-2">
            <select
              value={creatingNew ? '__new__' : selectedSearchId}
              onChange={(e) => {
                if (e.target.value === '__new__') { setCreatingNew(true); return; }
                setCreatingNew(false);
                setSelectedSearchId(e.target.value);
                setPreviewStatus(null);
                setImportResult(null);
              }}
              className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500"
            >
              <option value="" disabled>Seleziona una ricerca…</option>
              {searches.map((s) => (
                <option key={s.id} value={s.id}>{s.name} ({s.totalContents} contenuti)</option>
              ))}
              <option value="__new__">+ Nuova ricerca</option>
            </select>
          </div>
        )}
      </div>

      {/* Nuova ricerca inline */}
      {creatingNew && (
        <div className="bg-gray-800/40 border border-gray-800 rounded-lg p-4 space-y-3">
          <div>
            <label className="block text-xs text-gray-400 mb-1.5">Nome ricerca</label>
            <input
              type="text" value={newName} onChange={(e) => setNewName(e.target.value)}
              placeholder="es. Energia Nucleare"
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1.5">Query booleana</label>
            <input
              type="text" value={newBooleanExpr} onChange={(e) => setNewBooleanExpr(e.target.value)}
              placeholder="es. (nucleare OR atomica) AND energia"
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1.5">Canali da tracciare</label>
            <ChipSelect options={PULSAR_HISTORIC_CATEGORIES} selected={newCategories} onChange={setNewCategories} />
          </div>
          {createError && (
            <div className="flex items-center gap-2 text-red-400 text-sm bg-red-900/20 border border-red-800 rounded-lg px-4 py-2">
              <AlertCircle className="w-4 h-4 shrink-0" /> {createError}
            </div>
          )}
          <button
            onClick={handleCreateSearch}
            disabled={!newName.trim() || !newBooleanExpr.trim() || newCategories.length === 0 || createLoading}
            className="w-full bg-purple-700 hover:bg-purple-600 disabled:bg-gray-700 disabled:text-gray-500 text-white text-sm py-2 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
          >
            {createLoading ? <><Loader2 className="w-4 h-4 animate-spin" /> Creazione…</> : 'Crea ricerca'}
          </button>
        </div>
      )}

      {selectedSearch && (
        <>
          {/* Periodo */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-400 mb-1.5 flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> Da</label>
              <input
                type="date" value={startDate} onChange={(e) => { setStartDate(e.target.value); setPreviewStatus(null); }}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1.5">A</label>
              <input
                type="date" value={endDate} onChange={(e) => { setEndDate(e.target.value); setPreviewStatus(null); }}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500"
              />
            </div>
          </div>

          {/* Canali per l'import */}
          <div>
            <label className="block text-xs text-gray-400 mb-1.5">Canali</label>
            <ChipSelect
              options={PULSAR_HISTORIC_CATEGORIES}
              selected={previewCategories}
              onChange={(next) => { setPreviewCategories(next); setPreviewStatus(null); }}
            />
          </div>

          {/* Filtri granulari — condizionali per tipologia di canale selezionata.
              Si applicano solo in import: Pulsar non li supporta nella fase di
              stima (CreateHistoricInput accetta solo categorie+periodo). */}
          <div className="bg-gray-800/40 border border-gray-800 rounded-lg p-4 space-y-4">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Filtri avanzati (si applicano solo all'import, non alla stima sopra)</p>

            {/* Notizie online — tier fonte + sottotipo */}
            {showNewsFilters && (
              <div className="space-y-2">
                <label className="block text-xs text-gray-400">Tier fonte (notizie online)</label>
                <div className="flex flex-wrap gap-1.5">
                  {sourceTiers.map((t) => (
                    <button
                      key={t.key}
                      onClick={() => toggleCategory(selectedTiers, setSelectedTiers, t.key)}
                      title={`${t.domainCount} domini nel registro`}
                      className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                        selectedTiers.includes(t.key)
                          ? 'bg-purple-700 border-purple-500 text-white'
                          : 'bg-gray-800 border-gray-700 text-gray-400 hover:border-gray-500'
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
                <label className="block text-xs text-gray-400 mt-2">Sottotipo notizia</label>
                <div className="flex flex-wrap gap-1.5">
                  {NEWS_SUBTYPES.map((s) => (
                    <button
                      key={s.value}
                      onClick={() => toggleCategory(selectedNewsSubtypes, setSelectedNewsSubtypes, s.value)}
                      className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                        selectedNewsSubtypes.includes(s.value)
                          ? 'bg-purple-700 border-purple-500 text-white'
                          : 'bg-gray-800 border-gray-700 text-gray-400 hover:border-gray-500'
                      }`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Media reach — notizie online + broadcast (TV/radio/podcast) */}
            {showReachFilter && (
              <RangeInput label="Media reach (notizie/TV/radio/podcast)" value={mediaReach} onChange={setMediaReach} />
            )}

            {/* Engagement + follower — canali social/community */}
            {showSocialFilters && (
              <>
                <RangeInput label="Engagement (like + condivisioni + commenti)" value={engagements} onChange={setEngagements} />
                <RangeInput label="Follower autore" value={followers} onChange={setFollowers} />
              </>
            )}

            {/* Sentiment e credibilità — universali, qualunque canale */}
            <div className="space-y-2">
              <label className="block text-xs text-gray-400">Sentiment</label>
              <div className="flex flex-wrap gap-1.5">
                {SENTIMENTS.map((s) => (
                  <button
                    key={s.value}
                    onClick={() => toggleCategory(selectedSentiments, setSelectedSentiments, s.value)}
                    className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                      selectedSentiments.includes(s.value)
                        ? 'bg-purple-700 border-purple-500 text-white'
                        : 'bg-gray-800 border-gray-700 text-gray-400 hover:border-gray-500'
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
              <label className="block text-xs text-gray-400 mt-2">Credibilità contenuto (NewsGuard/Pulsar)</label>
              <div className="flex flex-wrap gap-1.5">
                {CREDIBILITY_LABELS.map((c) => (
                  <button
                    key={c.value}
                    onClick={() => toggleCategory(selectedCredibility, setSelectedCredibility, c.value)}
                    className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                      selectedCredibility.includes(c.value)
                        ? 'bg-purple-700 border-purple-500 text-white'
                        : 'bg-gray-800 border-gray-700 text-gray-400 hover:border-gray-500'
                    }`}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {previewError && (
            <div className="flex items-center gap-2 text-red-400 text-sm bg-red-900/20 border border-red-800 rounded-lg px-4 py-2">
              <AlertCircle className="w-4 h-4 shrink-0" /> {previewError}
            </div>
          )}

          {previewStatus && (
            <div className="bg-gray-800/60 border border-gray-700 rounded-lg px-4 py-3 text-sm">
              {previewStatus.ready
                ? <span className="text-green-400 font-medium">~{previewStatus.previewResult} contenuti stimati nel periodo</span>
                : <span className="flex items-center gap-2 text-gray-400"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Stima in corso (status: {previewStatus.status})…</span>}
            </div>
          )}

          <button
            onClick={handlePreview}
            disabled={previewCategories.length === 0 || previewLoading || (!!previewStatus && !previewStatus.ready)}
            className="w-full bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 disabled:text-gray-600 text-white text-sm py-2.5 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
          >
            {previewLoading ? <><Loader2 className="w-4 h-4 animate-spin" /> Calcolo anteprima…</> : 'Anteprima volume'}
          </button>

          {/* Import — abilitato solo dopo una preview pronta */}
          <div className={previewStatus?.ready ? '' : 'opacity-50 pointer-events-none'}>
            <label className="block text-xs text-gray-400 mb-1.5">
              Numero massimo da importare: <span className="text-white font-semibold">{limit}</span>
              <span className="text-gray-600 ml-1">(selezionati per engagement decrescente se il volume eccede)</span>
            </label>
            <input
              type="range" min={5} max={100} step={5} value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
              className="w-full accent-purple-500 mb-3"
            />

            {importError && (
              <div className="flex items-center gap-2 text-red-400 text-sm bg-red-900/20 border border-red-800 rounded-lg px-4 py-2 mb-3">
                <AlertCircle className="w-4 h-4 shrink-0" /> {importError}
              </div>
            )}

            {importResult && (
              <div className="bg-green-900/15 border border-green-800 rounded-lg px-4 py-3 space-y-2 mb-3">
                <div className="flex items-center gap-2 text-green-400 text-sm font-medium">
                  <CheckCircle className="w-4 h-4 shrink-0" />
                  {importResult.imported} contenuti importati in Inbox
                  {importResult.duplicates > 0 && ` (${importResult.duplicates} duplicati)`}
                  {importResult.skipped > 0 && ` (${importResult.skipped} saltati)`}
                </div>
                <button
                  onClick={() => navigate('/inbox')}
                  className="text-xs text-blue-400 hover:text-blue-300 transition-colors pl-6"
                >
                  Vai all'Inbox per approvare →
                </button>
              </div>
            )}

            <button
              onClick={handleImport}
              disabled={!previewStatus?.ready || importLoading}
              className="w-full bg-purple-700 hover:bg-purple-600 disabled:bg-gray-700 disabled:text-gray-500 text-white text-sm py-2.5 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
            >
              {importLoading ? <><Loader2 className="w-4 h-4 animate-spin" /> Import in corso…</> : 'Avvia import Pulsar'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ─── RSS tab (Phase F — placeholder) ─────────────────────────────────────────

function RssTab() {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-gray-500 space-y-3">
      <Rss className="w-12 h-12 text-gray-700" />
      <p className="text-sm">News RSS — disponibile nella Fase F</p>
    </div>
  );
}
